import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Typography,
  Button,
  Tag,
  Spin,
  message,
  Alert,
  Pagination,
} from 'antd';
import {
  TrophyOutlined,
  FileTextOutlined,
  ArrowLeftOutlined,
  BankOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { ThemeContext } from '../../context/ThemeContext';
import LandingLayout from './LandingLayout';
import './IssuerRatingsDetails.css';
import './Portfolio/StrategyCategorySelector.css';
import './Portfolio/HighReturnPage.css';
import apiClient from '../../services/apiClient';

const { Title, Text } = Typography;

const AGENCIES = ['care', 'crisil', 'icra', 'india', 'sp', 'fitch'];
const DEFAULT_PAGE_SIZE = 10;

const AGENCY_LABELS = {
  care: 'CARE',
  crisil: 'CRISIL',
  icra: 'ICRA',
  india: 'India Ratings',
  sp: 'S&P Global',
  fitch: 'Fitch',
};

const getAgencyColumns = (agency) => {
  const columns = [
    { key: 'instrument', label: 'Instrument' },
    { key: 'rating', label: 'Rating' },
    { key: 'date', label: 'Date' },
  ];
  if (agency === 'india' || agency === 'care') {
    columns.push({ key: 'amount', label: 'Amount' });
  }
  if (agency === 'fitch') {
    columns.push({ key: 'action', label: 'Action' });
  }
  columns.push({ key: 'rationale', label: 'Rationale' });
  return columns;
};

const getAgencyGridTemplate = (agency) => {
  const templates = {
    care: 'minmax(140px, 1.6fr) minmax(90px, 0.9fr) minmax(100px, 0.8fr) minmax(90px, 0.75fr) minmax(100px, 0.85fr)',
    india: 'minmax(140px, 1.6fr) minmax(90px, 0.9fr) minmax(100px, 0.8fr) minmax(90px, 0.75fr) minmax(100px, 0.85fr)',
    fitch: 'minmax(140px, 1.6fr) minmax(90px, 0.9fr) minmax(100px, 0.8fr) minmax(90px, 0.75fr) minmax(100px, 0.85fr)',
    default: 'minmax(140px, 1.6fr) minmax(90px, 0.9fr) minmax(100px, 0.8fr) minmax(100px, 0.85fr)',
  };
  return templates[agency] || templates.default;
};

const cleanRatingText = (rating) => {
  if (!rating) return '';
  return String(rating)
    .replace(/;\s*Stable/gi, '')
    .replace(/;\s*Positive/gi, '')
    .replace(/;\s*Negative/gi, '')
    .replace(/;\s*Watch/gi, '')
    .replace(/;\s*Outlook/gi, '')
    .replace(/\s*\(Stable\)/gi, '')
    .replace(/\s*\(Positive\)/gi, '')
    .replace(/\s*\(Negative\)/gi, '')
    .replace(/\s*\(Watch\)/gi, '')
    .trim();
};

const getRatingTone = (rating) => {
  if (!rating) return 'default';
  const value = rating.toString().toUpperCase();
  if (value.includes('AAA')) return 'excellent';
  if (value.includes('AA')) return 'strong';
  if (value.includes('A')) return 'good';
  if (value.includes('BBB')) return 'moderate';
  if (value.includes('BB')) return 'weak';
  if (value.includes('B')) return 'poor';
  if (value.includes('WD') || value.includes('WITHDRAWN')) return 'withdrawn';
  return 'default';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const IssuerRatingsDetails = () => {
  const { issuerName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useContext(ThemeContext);

  const [agencySummary, setAgencySummary] = useState(null);
  const [agencyRows, setAgencyRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeAgency, setActiveAgency] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalAgencyElements, setTotalAgencyElements] = useState(0);

  const currentIssuerName = useMemo(() => {
    if (issuerName) return decodeURIComponent(issuerName);
    if (location.state?.issuerName) return location.state.issuerName;
    return '';
  }, [issuerName, location.state?.issuerName]);

  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  const fetchAgencySummary = useCallback(async () => {
    if (!currentIssuerName) {
      setError('No issuer name provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `${baseUrl}/ratings/issuer/${encodeURIComponent(currentIssuerName)}/summary`,
        { validateStatus: () => true }
      );

      if (response.status === 204) {
        setAgencySummary({});
        return;
      }

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = response.data;
      if (result.statusCode === 200 && result.data) {
        setAgencySummary(result.data);
      } else {
        setAgencySummary({});
      }
    } catch (err) {
      console.error('Error fetching ratings summary:', err);
      setError('Unable to load credit ratings for this issuer.');
      message.error('Error fetching ratings data');
      setAgencySummary({});
    } finally {
      setLoading(false);
    }
  }, [baseUrl, currentIssuerName]);

  const fetchAgencyRatings = useCallback(async () => {
    if (!currentIssuerName || !activeAgency) {
      setAgencyRows([]);
      setTotalAgencyElements(0);
      return;
    }

    setListLoading(true);
    try {
      const response = await apiClient.get(
        `${baseUrl}/ratings/issuer/${encodeURIComponent(currentIssuerName)}/${activeAgency}`,
        {
          params: { page: currentPage - 1, size: pageSize },
          validateStatus: () => true,
        }
      );

      if (response.status === 204) {
        setAgencyRows([]);
        setTotalAgencyElements(0);
        return;
      }

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = response.data;
      if (result.statusCode === 200 && result.data) {
        setAgencyRows(result.data.content || []);
        setTotalAgencyElements(result.data.totalElements ?? 0);
      } else {
        setAgencyRows([]);
        setTotalAgencyElements(0);
      }
    } catch (err) {
      console.error('Error fetching agency ratings:', err);
      message.error('Error fetching ratings list');
      setAgencyRows([]);
      setTotalAgencyElements(0);
    } finally {
      setListLoading(false);
    }
  }, [activeAgency, baseUrl, currentIssuerName, currentPage, pageSize]);

  useEffect(() => {
    fetchAgencySummary();
  }, [fetchAgencySummary]);

  useEffect(() => {
    fetchAgencyRatings();
  }, [fetchAgencyRatings]);

  const availableAgencies = useMemo(
    () => AGENCIES.filter((agency) => (agencySummary?.[agency]?.totalElements ?? 0) > 0),
    [agencySummary]
  );

  const totalRatings = useMemo(
    () => availableAgencies.reduce(
      (sum, agency) => sum + (agencySummary?.[agency]?.totalElements ?? 0),
      0
    ),
    [availableAgencies, agencySummary]
  );

  useEffect(() => {
    if (availableAgencies.length === 0) {
      setActiveAgency(null);
      return;
    }
    if (!activeAgency || !availableAgencies.includes(activeAgency)) {
      setActiveAgency(availableAgencies[0]);
      setCurrentPage(1);
    }
  }, [availableAgencies, activeAgency]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(totalAgencyElements / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalAgencyElements, pageSize, currentPage]);

  const handleAgencyChange = (agency) => {
    setActiveAgency(agency);
    setCurrentPage(1);
  };

  const handleRetry = () => {
    fetchAgencySummary();
    if (activeAgency) {
      fetchAgencyRatings();
    }
  };

  const handleRatingRationale = (rating) => {
    const url =
      rating.rationaleUrl ||
      rating.downloadUrl ||
      rating.reportUrl ||
      rating.pressReleaseUrl ||
      rating.fileUrl;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      message.info('No rationale document available');
    }
  };

  const getAmountDisplay = (record, agency) => {
    if (agency === 'india') {
      return record.amount ? `₹${record.amount} Cr` : null;
    }
    if (agency === 'care') {
      return record.ratingAmount ? `₹${record.ratingAmount} Cr` : null;
    }
    return null;
  };

  const renderRatingCell = (columnKey, record, agency) => {
    const rating = record.id?.rating || record.rating || '—';
    const instrument = record.id?.instrument || record.instrument || '—';
    const date = formatDate(record.id?.date || record.date);
    const amount = getAmountDisplay(record, agency);
    const actionText = record.ratingActionDescription;
    const hasRationale =
      record.rationaleUrl ||
      record.downloadUrl ||
      record.reportUrl ||
      record.pressReleaseUrl ||
      record.fileUrl;

    switch (columnKey) {
      case 'instrument':
        return (
          <span className="issuer-ratings-cell__instrument" title={instrument}>
            {instrument}
          </span>
        );
      case 'rating':
        return (
          <span className={`issuer-ratings-row__rating issuer-ratings-row__rating--${getRatingTone(rating)}`}>
            {rating}
          </span>
        );
      case 'date':
        return <span className="issuer-ratings-cell__date">{date}</span>;
      case 'amount':
        return <span className="issuer-ratings-cell__amount">{amount || '—'}</span>;
      case 'action':
        return actionText ? (
          <Tag className="issuer-ratings-row__action-tag">{actionText}</Tag>
        ) : (
          <span className="issuer-ratings-row__na">—</span>
        );
      case 'rationale':
        return hasRationale ? (
          <Button
            type="link"
            size="small"
            icon={<FileTextOutlined />}
            className="issuer-ratings-row__rationale"
            onClick={() => handleRatingRationale(record)}
          >
            View
          </Button>
        ) : (
          <span className="issuer-ratings-row__na">—</span>
        );
      default:
        return '—';
    }
  };

  const renderRatingRow = (record, agency, index, columns) => (
    <div
      className="issuer-ratings-row"
      key={`${agency}-${index}`}
      style={{ gridTemplateColumns: getAgencyGridTemplate(agency) }}
    >
      {columns.map((column) => (
        <div key={column.key} className="issuer-ratings-cell" data-label={column.label}>
          {renderRatingCell(column.key, record, agency)}
        </div>
      ))}
    </div>
  );

  const renderAgencySelector = () => {
    const agencyCount = availableAgencies.length;
    const singleAgency = agencyCount === 1 ? availableAgencies[0] : null;

    if (singleAgency) {
      const summary = agencySummary[singleAgency];
      const rating = cleanRatingText(summary?.highestRating);
      const date = formatDate(summary?.highestRatingDate);
      const label = AGENCY_LABELS[singleAgency] || singleAgency.toUpperCase();
      const count = summary?.totalElements ?? 0;

      return (
        <section className="strategy-category-panel issuer-ratings-agency-panel" aria-label="Rating agency">
          <div className="strategy-category-panel__header">
            <div className="strategy-category-panel__heading">
              <TrophyOutlined className="strategy-category-panel__icon" aria-hidden />
              <div>
                <span className="strategy-category-panel__eyebrow">Rating Agency</span>
                <h2 className="strategy-category-panel__title">{label}</h2>
              </div>
            </div>
            <span className="strategy-category-panel__badge">1 agency</span>
          </div>

          <div className="strategy-category-single issuer-ratings-agency-single">
            <span className="strategy-category-single__label">Highest rating</span>
            <span className="strategy-category-single__name">{rating || '—'}</span>
            <span className="issuer-ratings-agency-single__meta">
              {count} rating
              {count === 1 ? '' : 's'} · {date}
            </span>
          </div>
        </section>
      );
    }

    return (
      <section className="strategy-category-panel issuer-ratings-agency-panel" aria-label="Rating agency selection">
        <div className="strategy-category-panel__header">
          <div className="strategy-category-panel__heading">
            <TrophyOutlined className="strategy-category-panel__icon" aria-hidden />
            <div>
              <span className="strategy-category-panel__eyebrow">Rating Agency</span>
              <h2 className="strategy-category-panel__title">Choose an agency to view ratings</h2>
            </div>
          </div>
          <span className="strategy-category-panel__badge">
            {agencyCount} agencies
          </span>
        </div>

        <div
          className="strategy-category-grid issuer-ratings-agency-grid"
          role="tablist"
          aria-label="Rating agencies"
        >
          {availableAgencies.map((agency) => {
            const summary = agencySummary[agency];
            const rating = cleanRatingText(summary?.highestRating);
            const date = formatDate(summary?.highestRatingDate);
            const count = summary?.totalElements ?? 0;
            const isActive = agency === activeAgency;

            return (
              <button
                key={agency}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`strategy-category-card${isActive ? ' is-active' : ''}`}
                onClick={() => handleAgencyChange(agency)}
              >
                <span className="strategy-category-card__top">
                  <span className="strategy-category-card__name">
                    {AGENCY_LABELS[agency] || agency.toUpperCase()}
                  </span>
                  {isActive && (
                    <CheckCircleFilled className="strategy-category-card__check" aria-hidden />
                  )}
                </span>
                <span className="strategy-category-card__preview">
                  {rating || '—'} · {count} rating
                  {count === 1 ? '' : 's'} · {date}
                </span>
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  const renderAgencyPanel = () => {
    if (!activeAgency || !agencySummary?.[activeAgency]) return null;
    const rows = agencyRows;
    const columns = getAgencyColumns(activeAgency);
    const gridTemplate = getAgencyGridTemplate(activeAgency);
    const showingFrom = totalAgencyElements === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const showingTo = Math.min(currentPage * pageSize, totalAgencyElements);

    return (
      <Card className="issuer-ratings-panel-card">
        <div className="issuer-ratings-results">
          <span className="issuer-ratings-results__label">Rating results</span>
          <span className="issuer-ratings-results__count">
            <strong>{totalAgencyElements}</strong> rating{totalAgencyElements === 1 ? '' : 's'}
            {totalAgencyElements > pageSize && (
              <> · showing {showingFrom}–{showingTo}</>
            )}
          </span>
        </div>

        <Spin spinning={listLoading}>
          <div className="issuer-ratings-list">
            <div
              className="issuer-ratings-list__header"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {columns.map((column) => (
                <span key={column.key}>{column.label}</span>
              ))}
            </div>
            {rows.length === 0 && !listLoading ? (
              <div className="issuer-ratings-list__empty">No ratings found for this page.</div>
            ) : (
              rows.map((record, index) => renderRatingRow(record, activeAgency, index, columns))
            )}
          </div>
        </Spin>

        {totalAgencyElements > 0 && (
          <div className="hr-pagination issuer-ratings-pagination">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalAgencyElements}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              onShowSizeChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              showSizeChanger
              pageSizeOptions={['10', '20', '50']}
              className="hr-ant-pagination"
            />
          </div>
        )}
      </Card>
    );
  };

  const pageBody = loading ? (
    <div className="issuer-ratings-loading">
      <Spin size="large" />
      <Text>Loading credit ratings…</Text>
    </div>
  ) : error ? (
    <div className="issuer-ratings-error">
      <Alert
        message="Unable to load ratings"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={handleRetry}>
            Retry
          </Button>
        }
      />
    </div>
  ) : (
    <>
      <div className="issuer-ratings-metrics">
        <div className="issuer-ratings-metrics__inner">
          <div className="issuer-ratings-metric">
            <span className="issuer-ratings-metric__label">Agencies</span>
            <span className="issuer-ratings-metric__value accent">{availableAgencies.length}</span>
          </div>
          <div className="issuer-ratings-metric-divider" aria-hidden />
          <div className="issuer-ratings-metric">
            <span className="issuer-ratings-metric__label">Total ratings</span>
            <span className="issuer-ratings-metric__value">{totalRatings}</span>
          </div>
          <div className="issuer-ratings-metric-divider" aria-hidden />
          <div className="issuer-ratings-metric">
            <span className="issuer-ratings-metric__label">Issuer</span>
            <span className="issuer-ratings-metric__value">{currentIssuerName || '—'}</span>
          </div>
        </div>
      </div>

      <div className="issuer-ratings-content">
        {availableAgencies.length === 0 ? (
          <div className="issuer-ratings-empty">
            <TrophyOutlined className="issuer-ratings-empty__icon" />
            <h3>No credit ratings found</h3>
            <p>This issuer does not have ratings from supported agencies yet.</p>
            <Button type="default" onClick={() => navigate(-1)}>
              Go back
            </Button>
          </div>
        ) : (
          <>
            {renderAgencySelector()}
            {renderAgencyPanel()}
          </>
        )}
      </div>
    </>
  );

  return (
    <LandingLayout>
      <div className={`issuer-ratings-page hr-page ${isDark ? 'dark' : ''}`}>
        <header className="issuer-ratings-header">
          <div className="issuer-ratings-header__inner">
            <nav className="issuer-ratings-breadcrumb" aria-label="Breadcrumb">
              <button
                type="button"
                className="issuer-ratings-breadcrumb__link"
                onClick={() => navigate('/issuer-categorization')}
              >
                Issuers
              </button>
              <span className="issuer-ratings-breadcrumb__sep" aria-hidden>›</span>
              <button
                type="button"
                className="issuer-ratings-breadcrumb__link"
                onClick={() => navigate(-1)}
              >
                {currentIssuerName || 'Issuer'}
              </button>
              <span className="issuer-ratings-breadcrumb__sep" aria-hidden>›</span>
              <span className="issuer-ratings-breadcrumb__current">Credit Ratings</span>
            </nav>

            <div className="issuer-ratings-title-row">
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                className="issuer-ratings-btn-back"
              >
                Back
              </Button>
              <div className="issuer-ratings-title-block">
                <Title level={2} className="issuer-ratings-title">
                  <TrophyOutlined /> Credit Ratings
                </Title>
                <Text type="secondary" className="issuer-ratings-subtitle">
                  <BankOutlined /> {currentIssuerName || 'Issuer'}
                </Text>
              </div>
            </div>
          </div>
        </header>

        {pageBody}
      </div>
    </LandingLayout>
  );
};

export default IssuerRatingsDetails;
